import { Link } from "react-router-dom";
import { PageIntro, PageBadgeBand, Section } from "@/components/site/PageShell";
import { SITE } from "@/constants/testIds";
import { useMeta } from "@/hooks/useMeta";

/**
 * PORTED FROM v3.0 privacy.html (2026-03-30) and reconciled with the
 * current tiered vault model. Previously-flagged [LEGAL REVIEW]
 * contradictions have been resolved in-line with the confirmed product
 * behavior.
 */

const SECTIONS = [
  {
    id: "overview",
    heading: "Overview",
    body: (
      <>
        <p>
          This is a legally binding agreement between you and Simple Concepts
          LLC. By creating an account or using the app, you accept the terms.
          If you do not agree, do not use YeboNadi.
        </p>
        <p className="text-[color:var(--yn-muted)] text-sm">
          Effective date: 30 March 2026 · Agreement v3.0 · Data Controller:
          Simple Concepts LLC · Platforms: Apple iOS 15+ and Android 10+
        </p>
      </>
    ),
  },
  {
    id: "what-yebonadi-does",
    heading: "What YeboNadi does",
    body: (
      <>
        <p>
          YeboNadi is not a dictaphone app, it will record every verbal
          interaction, both yours and those of the others you are
          interacting with, once you start a session.
        </p>
        <p>
          It is built around a privacy-first, court-order and
          signed-release access-only architecture. Recordings are encrypted
          on the device when created and uploaded in encrypted form.
          Recordings cannot be played back or retrieved by anyone,
          including the provider, unless there is a valid court order or a
          signed release, submitted along with the user&apos;s
          encryption/decryption key.
        </p>
        <p>
          <strong>Core principle.</strong> Once a recording is made, it is
          sealed.
        </p>
      </>
    ),
  },
  {
    id: "recording-behavior",
    heading: "Essential recording facts",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Two manual recording modes are supported:
            <ul className="list-[circle] pl-6 mt-2 space-y-1.5">
              <li>
                <strong>Interruptible</strong>, records until you stop the
                session, but halts if another app takes audio focus or the
                device requires it (e.g. an incoming call). Best for
                everyday use.
              </li>
              <li>
                <strong>Non-interruptible</strong>, records continuously
                until you manually stop it or the phone dies. Best for
                high-stakes situations where the record must not pause.
              </li>
            </ul>
          </li>
          <li>
            Recordings are encrypted on-device, in transit, and at rest
            using AES-256 and TLS 1.3 or higher.
          </li>
          <li>
            The user encryption/decryption key (or the passphrase used to
            derive it) is irrecoverable and cannot be reset by YeboNadi.
            Share it with one or many very trusted persons before you need
            it.
          </li>
          <li>
            There is no playback, export, or self-retrieval inside the
            application after capture. Retrieval requires a legal order or
            a signed release along with your encryption/decryption key; on
            approval, YeboNadi decrypts the record and delivers it via
            secure cloud storage. A retrieval fee applies.
          </li>
          <li>
            Cloud storage can overwrite older recordings when storage is
            full unless items are explicitly marked &ldquo;Keep.&rdquo;
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "what-we-collect",
    heading: "What we collect",
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Account data</strong>, email address, device identifier,
          authentication credentials.
        </li>
        <li>
          <strong>Technical data</strong>, device model, OS version, app
          version, crash diagnostics, and error logs.
        </li>
        <li>
          <strong>Recording metadata</strong>, encrypted file references,
          location, timestamps, duration, and internal identifiers.
        </li>
        <li>
          <strong>Usage analytics</strong>, aggregated or anonymized feature
          and location usage data to improve the application.
        </li>
        <li>
          <strong>Communications</strong>, support messages sent to the
          company.
        </li>
        <li>
          YeboNadi does not sell personal data and does not use it for
          advertising or third-party profiling.
        </li>
      </ul>
    ),
  },
  {
    id: "security",
    heading: "Security measures",
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>AES-256 device-side encryption before upload.</li>
        <li>TLS 1.3 encrypted transmission to secure cloud storage.</li>
        <li>Strict role-based access controls and audit logging.</li>
        <li>
          Court-order or signed-release gated decryption, with the
          user&apos;s encryption/decryption key required.
        </li>
        <li>Regular independent security audits and penetration testing.</li>
        <li>ISO 27001-aligned information security management practices.</li>
      </ul>
    ),
  },
  {
    id: "retention",
    heading: "Retention",
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          Encrypted recordings are retained for the duration of the active
          subscription, subject to the missed-payment and deletion-request
          rules in the agreement.
        </li>
        <li>
          Account and identity data may be retained for seven years after
          closure.
        </li>
        <li>
          Technical data: rolling twelve months unless needed for proceedings.
        </li>
        <li>Support communications: three years from case closure.</li>
        <li>Pseudonymized analytics: up to two years.</li>
      </ul>
    ),
  },
  {
    id: "your-rights",
    heading: "Your privacy rights",
    body: (
      <p>
        Depending on jurisdiction, you have rights of access, rectification,
        erasure, restriction, portability, objection, and withdrawal of
        consent, as well as POPIA (South Africa) and CCPA (California) rights.
        Requests go to{" "}
        <a
          href="mailto:privacy@concepts-simplified.com"
          className="text-[color:var(--yn-primary)] hover:underline"
        >
          privacy@concepts-simplified.com
        </a>{" "}
        and may require identity verification.
      </p>
    ),
  },
  {
    id: "your-responsibilities",
    heading: "Your responsibilities",
    body: (
      <p>
        You are responsible for complying with recording-consent laws,
        obtaining any required consent, using the verbal disclosure mechanism
        where appropriate, avoiding unlawful surveillance or harassment, and
        not attempting to bypass technical protections. These are the same
        commitments you make in the{" "}
        <Link
          to="/undertaking"
          className="text-[color:var(--yn-primary)] hover:underline"
        >
          Undertaking
        </Link>
        .
      </p>
    ),
  },
  {
    id: "third-parties",
    heading: "Third-party providers & international transfers",
    body: (
      <p>
        YeboNadi uses in-house or trusted cloud and infrastructure providers
        under data processing agreements. Data may be processed internationally
        using safeguards such as SCCs, UK IDTAs, POPIA cross-border
        conditions, transfer impact assessments, and the EU-US Data Privacy
        Framework where applicable.
      </p>
    ),
  },
  {
    id: "contact",
    heading: "Contact",
    body: (
      <>
        <p>Simple Concepts LLC</p>
        <p>2820 S Alma School Rd, Suite 18, PMB 2034, Chandler, AZ 85286</p>
        <p>
          <a
            href="mailto:privacy@concepts-simplified.com"
            className="text-[color:var(--yn-primary)] hover:underline"
          >
            privacy@concepts-simplified.com
          </a>
          {" · "}
          <a
            href="mailto:info@concepts-simplified.com"
            className="text-[color:var(--yn-primary)] hover:underline"
          >
            info@concepts-simplified.com
          </a>
          {" · "}
          <a
            href="tel:+18324231808"
            className="text-[color:var(--yn-primary)] hover:underline"
          >
            +1 (832) 423-1808
          </a>
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  useMeta({
    title: "Privacy Policy, YeboNadi",
    description:
      "How YeboNadi collects, secures, and retains your data. Encrypted on-device (AES-256), transmitted over TLS 1.3, stored with strict access controls. What we can and can't see. POPIA + CCPA rights honored.",
    path: "/privacy",
  });

  return (
    <div data-testid={SITE.privacyPage}>
      <PageIntro
        kicker="Privacy"
        title="What we collect. What we don't."
        lede="Ported from the in-app agreement v3.0 (30 March 2026) and reconciled with the current court-order and signed-release access-only architecture. No court-admissibility claims are made; where the agreement and the product mechanism describe the same behavior, both use the same words."
      />

      <Section className="!pt-0">
        <div className="max-w-3xl">
          {/* On-page TOC */}
          <nav aria-label="On this page" className="mb-10">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-muted)] mb-3">
              On this page
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Explanation callout removed, all v3.0 contradictions have been reconciled. */}

          <div className="space-y-14">
            {SECTIONS.map((s) => (
              <article key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[color:var(--yn-ink)] tracking-tight">
                  {s.heading}
                </h2>
                <div className="mt-4 space-y-4 text-[16px] leading-[1.75] text-[color:var(--yn-muted)]">
                  {s.body}
                </div>
              </article>
            ))}
          </div>
        </div>
      </Section>

      <PageBadgeBand heading="You control your key, you control your vault." />
    </div>
  );
}
